import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { isEmpty, omit } from 'underscore';
import { regenerateProject } from 'redux/middleware/api/creation-service/actions';
import { makeProjectAspectRatioSelector } from 'redux/modules/entities';
import { pushModal, replaceModal } from 'redux/modules/modal';
import { showError } from 'redux/modules/notification';
import { videoExportOptionsSelector } from 'redux/modules/user-pref/selectors';
import { Dispatch, State } from 'redux/types';
import { getAspectRatioName } from 'utils/aspect-ratio';
import { EditResult, ProjectPreviewModalResult } from './types';

export interface UseHandleEditFlowResult {
  startEditFlow: (projectId: string) => Promise<ProjectPreviewModalResult>;
}

export default function useHandleEditFlow(): UseHandleEditFlowResult {
  const dispatch = useDispatch<Dispatch>();

  const handleRegenerateProject = useCallback(
    async (projectId: string, editResult: EditResult): Promise<void> => {
      const { newEmbedConfig, captionSourceEddyProjectCuid } = editResult;

      if (!projectId || !newEmbedConfig) {
        return;
      }

      const aspectRatio = dispatch((_, getState) =>
        makeProjectAspectRatioSelector(projectId)(getState()),
      );

      const exportOptions = dispatch((_, getState) =>
        videoExportOptionsSelector(
          getAspectRatioName(aspectRatio?.width / aspectRatio?.height),
          false,
          getState() as State,
        ),
      );

      try {
        await dispatch(
          regenerateProject({
            ...exportOptions,
            captionSourceEddyProjectCuid,
            baseConfigJson: newEmbedConfig,
            projectId,
          }),
        );
      } catch {
        dispatch(showError('Something went wrong, please try again', 5));
      }
    },
    [dispatch],
  );

  const startEditFlow = useCallback(
    async (projectId: string): Promise<ProjectPreviewModalResult> => {
      const projectPreviewResult: ProjectPreviewModalResult = await dispatch(
        pushModal({
          name: 'ProjectPreview',
          params: {
            projectId,
          },
        }),
      );

      if (
        typeof projectPreviewResult === 'string' ||
        !projectPreviewResult ||
        isEmpty(projectPreviewResult)
      ) {
        return projectPreviewResult;
      }

      const editResult: EditResult = await dispatch(
        replaceModal({
          name: projectPreviewResult.action,
          params: omit(projectPreviewResult, 'action'),
        }),
      );

      if (editResult) {
        const result =
          projectPreviewResult.action === 'EditTemplate'
            ? editResult
            : { newEmbedConfig: projectPreviewResult.embedConfig };

        await handleRegenerateProject(projectId, result);
      }

      startEditFlow(projectId);

      return undefined;
    },
    [dispatch, handleRegenerateProject],
  );

  return {
    startEditFlow,
  };
}
